import React, { useMemo, memo } from 'react';
import { useTab } from 'contexts/TabContext';
import { getPageComponent } from './PageComponents';
import {
  TabAreaContainer,
  TabList,
  Tab,
  TabLabel,
  TabCloseButton,
  TabContent,
  EmptyState,
} from 'styles/common.styles';

// TabPanel — 활성 탭만 flex 레이아웃으로 전체 높이 사용
const TabPanel = ({ tab, isActive, TabComponent }) => {
  if (!TabComponent) return null;

  return (
    <div
      key={tab.id}
      style={{
        display: isActive ? 'flex' : 'none',
        flexDirection: 'column',
        width: '100%',
        height: '100%',
        minHeight: 0,
        overflow: 'hidden',
      }}
    >
      <TabComponent />
    </div>
  );
};

TabPanel.displayName = 'TabPanel';

const TabArea = memo(() => {
  const { tabs, activeTabId, closeTab, activateTab } = useTab();

  // 모든 탭의 컴포넌트를 미리 로드하여 메모이제이션
  // 탭이 변경되어도 컴포넌트가 언마운트되지 않도록 함
  const tabComponents = useMemo(() => {
    const components = {};
    tabs.forEach((tab) => {
      components[tab.id] = getPageComponent(tab.component, tab.componentPath);
    });
    return components;
  }, [tabs, tabs.map((t) => `${t.id}:${t.componentPath}:${t.refreshKey ?? 0}`).join('|')]);

  if (tabs.length === 0) {
    return (
      <TabAreaContainer>
        <TabList />
        <TabContent>
          <EmptyState>메뉴를 선택하여 탭을 열어주세요</EmptyState>
        </TabContent>
      </TabAreaContainer>
    );
  }

  return (
    <TabAreaContainer>
      <TabList>
        {tabs.map((tab) => (
          <Tab
            key={tab.id}
            $active={tab.id === activeTabId}
            onClick={() => activateTab(tab.id)}
          >
            <TabLabel>{tab.label}</TabLabel>
            <TabCloseButton
              onClick={(e) => {
                e.stopPropagation();
                closeTab(tab.id);
              }}
            >
              ×
            </TabCloseButton>
          </Tab>
        ))}
      </TabList>
      <TabContent>
        {tabs.length === 0 ? (
          <EmptyState>메뉴를 선택하여 탭을 열어주세요</EmptyState>
        ) : (
          <>
            {tabs.map((tab) => {
              const TabComponent = tabComponents[tab.id];
              return (
                <TabPanel
                  key={`${tab.id}-${tab.refreshKey ?? 0}`}
                  tab={tab}
                  isActive={tab.id === activeTabId}
                  TabComponent={TabComponent}
                />
              );
            })}
          </>
        )}
      </TabContent>
    </TabAreaContainer>
  );
});

TabArea.displayName = 'TabArea';

export default TabArea;

